/**
 * The audit log in plain words: what each kind of entry means, what each
 * kind of record is called, and field names as a person would say them.
 * The filters still send the server's own codes.
 */

/** Every AuditAction the server records. `Audit.test.tsx` checks none is missing. */
export const AUDIT_ACTIONS: Array<{ value: string; label: string }> = [
  { value: 'CREATE', label: 'Created' },
  { value: 'UPDATE', label: 'Changed' },
  { value: 'DELETE', label: 'Deleted' },
  { value: 'POST', label: 'Made final' },
  { value: 'REVERSE', label: 'Undone' },
  { value: 'APPROVE', label: 'Approved' },
  { value: 'REJECT', label: 'Rejected' },
  { value: 'LOGIN', label: 'Signed in' },
  { value: 'LOGIN_FAILED', label: 'Sign-in failed' },
  { value: 'LOGOUT', label: 'Signed out' },
  { value: 'PASSWORD_CHANGE', label: 'Password changed' },
  { value: 'PERMISSION_CHANGE', label: 'Access changed' },
  { value: 'ACCESS_DENIED', label: 'Access refused' },
  { value: 'EXPORT', label: 'Downloaded' },
  { value: 'PRINT', label: 'Printed' },
  { value: 'READ_SENSITIVE', label: 'Saw private details' },
  { value: 'SESSION_REVOKE', label: 'Signed out a device' },
  { value: 'UNLOCK', label: 'Unlocked the screen' },
  { value: 'UNLOCK_FAILED', label: 'Unlock failed' },
  { value: 'SYSTEM_RESET', label: 'Data reset' },
];

export const AUDIT_ACTION_LABEL = new Map(AUDIT_ACTIONS.map((a) => [a.value, a.label]));

/** Kinds of record, as the screens call them. */
export const ENTITY_LABELS: Record<string, string> = {
  Account: 'Account',
  AdvanceLoan: 'Advance or loan',
  Announcement: 'Announcement',
  AttendanceMonthOverride: 'Attendance correction',
  AttendanceRegister: 'Attendance',
  AuditLog: 'Audit log',
  BankAccount: 'Bank account',
  BankTransfer: 'Bank transfer',
  Buyer: 'Buyer',
  CashBox: 'Cash box',
  CashVoucher: 'Cash book entry',
  CompanySettings: 'Company settings',
  ConsumptionLog: 'Stock OUT',
  Department: 'Department',
  Designation: 'Designation',
  DispatchBill: 'Dispatch bill',
  Employee: 'Staff record',
  EmployeeRecurringItem: 'Pay item',
  ExpenseCategory: 'Expense category',
  Farm: 'Farm',
  FarmExpense: 'Farm expense',
  FinalSettlement: 'Final settlement',
  FinanceSettings: 'Finance settings',
  FiscalPeriod: 'Accounting month',
  FiscalYear: 'Accounting year',
  Flock: 'Flock',
  FlockDailyRegister: 'Daily register',
  FuelReading: 'Fuel reading',
  GatePass: 'Dispatch',
  Holiday: 'Holiday',
  HolidayRule: 'Holiday rule',
  HrReport: 'HR report',
  HrSettings: 'HR settings',
  InwardShipment: 'Stock IN',
  JournalEntry: 'Journal entry',
  LeaveBalance: 'Leave balance',
  LeaveRequest: 'Leave request',
  LeaveType: 'Leave type',
  OtherSale: 'Other sale',
  PayComponent: 'Pay part',
  PayrollRun: 'Payroll',
  Payslip: 'Pay slip',
  Pen: 'Pen',
  Receipt: 'Receipt',
  Reminder: 'Reminder',
  Role: 'Role',
  SalaryRevision: 'Pay change',
  Section: 'Section',
  Shed: 'Shed',
  Shift: 'Shift',
  Sku: 'Item',
  SkuCategory: 'Item category',
  StatutoryYear: 'Tax year',
  StockAdjustment: 'Stock adjustment',
  StockTransfer: 'Transfer',
  StorageBin: 'Storage bin',
  Store: 'Store',
  Supplier: 'Supplier',
  SystemReset: 'Data reset',
  Uom: 'Unit',
  User: 'Person',
  UserPreferences: 'Display settings',
};

/** "InwardShipment" → "Stock IN"; anything unknown → "Inward shipment". */
export function entityLabel(entity: string): string {
  return ENTITY_LABELS[entity] ?? humanise(entity);
}

/** "fullName" → "Full name", "farm_id" → "Farm id". */
export function humanise(name: string): string {
  const words = name
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
